import { useEffect, useState, type FC } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { IconSettings, IconStopwatch } from '@tabler/icons-react';

import {
  Banner,
  BannerBrandSuite,
  BannerBrandLogo,
  BannerBrandInline,
  BannerSearch,
  BannerRight,
  Fishbowl,
  ButtonFieldIcon,
  Iconsvg,
  SrOnly,
  BannerStatus,
} from '@wk/components-react16';
import { useMsal } from '@azure/msal-react';

import { InteractionStatus } from '@azure/msal-browser';

import { useShallow } from 'zustand/react/shallow';

import { callMsGraph } from '../../lib/MsGraphApi';
import useOnGoingEvaluationsStore from '@/stores/ongoingEvaluations';

import type { GraphData, UserRoleAccess } from '@/types';
import { UserRole } from '@/types';

import './styles.css';

interface HeaderState {
  graphData: GraphData | null;
  userPhoto: string | null;
  isFetchingGraphData: boolean;
  hasUserPhoto: boolean;
}

const Header: FC = () => {
  const queryClient = useQueryClient();

  const userRoleAccess = queryClient.getQueryData<
    AxiosResponse<UserRoleAccess>
  >(['userRoleAccess']);

  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const [state, setState] = useState<HeaderState>({
    graphData: null,
    userPhoto: null,
    isFetchingGraphData: false,
    hasUserPhoto: false,
  });

  const handleStateUpdate = (newState: Partial<HeaderState>) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  };

  const { ongoingEvaluations, setIsOngoingEvaluationsOpen } =
    useOnGoingEvaluationsStore(
      useShallow((state) => ({
        ongoingEvaluations: state.ongoingEvaluations,
        setIsOngoingEvaluationsOpen: state.setIsOngoingEvaluationsOpen,
      })),
    );

  useEffect(() => {
    let objectUrl: string | undefined;
    if (!state.graphData && inProgress === InteractionStatus.None) {
      handleStateUpdate({ isFetchingGraphData: true });
      callMsGraph()
        .then(([graphData, userPhotoBlob, hasPhoto]) => {
          if (hasPhoto && userPhotoBlob) {
            objectUrl = (window.URL || window.webkitURL).createObjectURL(
              userPhotoBlob,
            );
          }
          handleStateUpdate({
            graphData,
            userPhoto: objectUrl ?? null,
            hasUserPhoto: !!objectUrl,
            isFetchingGraphData: false,
          });
        })
        .catch(() => {
          handleStateUpdate({
            isFetchingGraphData: false,
            userPhoto: null,
            hasUserPhoto: false,
          });
        });
    }
  }, [inProgress, instance, state.graphData]);

  return (
    <Banner mode={'workflow'}>
      <BannerBrandSuite
        srLabel="Pegasus ORR"
        isPreventDefault
        onBrandClick={() => {
          navigate('/');
        }}
        slot="bannerBrandSuite"
      >
        <BannerBrandLogo
          xsLogoSrc={
            'https://cdn.wolterskluwer.io/wk/jumpstart-v3-assets/0.x.x/logo/wheel-small.svg'
          }
          logoSrc={
            'https://cdn.wolterskluwer.io/wk/jumpstart-v3-assets/0.x.x/logo/wheel-medium.svg'
          }
          slot="bannerBrandLogo"
        ></BannerBrandLogo>
        <BannerBrandInline
          productName={'Pegasus'}
          childProductName={'Operational Readiness Review'}
          slot="bannerBrand"
        ></BannerBrandInline>
      </BannerBrandSuite>
      <BannerSearch slot="bannerSearch">
        <div slot="bannerSearchMobile">
          <ButtonFieldIcon size="large24px">
            <button type="button" id="searchMobile">
              <Iconsvg name="search"></Iconsvg>
              <Iconsvg name="search" isFill></Iconsvg>
              <SrOnly>Open search</SrOnly>
            </button>
          </ButtonFieldIcon>
        </div>
      </BannerSearch>
      <BannerRight slot="bannerRight">
        {userRoleAccess?.data.user.roles.includes(
          UserRole.CHECKPOINT_MANAGER,
        ) && (
            <ButtonFieldIcon>
              <button type="button" onClick={() => navigate('/settings')}>
                <IconSettings size={24} />
              </button>
            </ButtonFieldIcon>
          )}
        <ButtonFieldIcon>
          <button
            type="button"
            className="relative"
            onClick={() => {
              setIsOngoingEvaluationsOpen(true);
            }}
          >
            <IconStopwatch size={24} />
            {ongoingEvaluations.length > 0 && (
              <span className="absolute top-3 right-3 animate-pulse bg-red-500 text-white text-xs rounded-full w-2 h-2 flex items-center justify-center"></span>
            )}
          </button>
        </ButtonFieldIcon>
        {state.isFetchingGraphData ? (
          <>
            <BannerStatus
              status={'pending'}
              srLabel={'Fetching user details'}
            ></BannerStatus>
          </>
        ) : (
          <>
            <Fishbowl
              userName={state.graphData?.displayName ?? ''}
              onFishbowlClick={() => { }}
              hasIcon={!state.hasUserPhoto}
            >
              {state.hasUserPhoto ? (
                <img
                  alt={state.graphData?.displayName ?? ''}
                  src={state.userPhoto ?? ''}
                />
              ) : (
                <Iconsvg name="user" isFill />
              )}
            </Fishbowl>
          </>
        )}
      </BannerRight>
    </Banner>
  );
};

export default Header;
